import React, {
  createContext,
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ReactNode, useEffect, useRef } from "react";
import { useMap } from "../../hooks/incontext/useMap";
import { MAP_TOOL_LAYOUT } from "src/domains/shared/constants/mapToolButton";
import { Control } from "ol/control";
import type { ToolGuideRow } from "src/domains/shared/utils/toolGuideMessage";

export interface ControlSectionProps {
  children?: ReactNode;
  style?: CSSProperties;
}

/** 활성 도구가 커서 안내 칩에 올리는 줄 목록 — `rows` 가 null 이면 칩을 그리지 않는다 */
export interface ToolGuideState {
  rows: ToolGuideRow[] | null;
}

export const NO_TOOL_GUIDE: ToolGuideState = { rows: null };

type ControlSectionContextProps = {
  selectedButtonId: string;
  selectButton: (id: string) => void;
  toolGuide: ToolGuideState;
  setToolGuide: (guide: ToolGuideState) => void;
};

const ControlSectionContext = createContext<ControlSectionContextProps | null>(
  null
);

export const useControlSection = () => {
  return useContext(ControlSectionContext) as ControlSectionContextProps;
};

const DEFAULT_SECTION_STYLE: CSSProperties = {
  position: "absolute",
  left: `${MAP_TOOL_LAYOUT.LEFT_PX}px`,
  top: `${MAP_TOOL_LAYOUT.FULLSCREEN_TOP_PX}px`,
  display: "flex",
  flexDirection: "column",
  gap: `${MAP_TOOL_LAYOUT.SECTION_GAP_PX}px`,
};

export const ControlSection = ({
  children,
  style = DEFAULT_SECTION_STYLE,
}: ControlSectionProps) => {
  const map = useMap();
  const ref = useRef<HTMLDivElement>(null);
  const [selectedButtonId, setSelectedButtonId] = useState("");
  const [toolGuide, setToolGuide] = useState<ToolGuideState>(NO_TOOL_GUIDE);

  const selectButton = useCallback((id: string) => {
    setSelectedButtonId(id);
  }, []);

  const contextValue = useMemo(
    () => ({
      selectedButtonId,
      selectButton,
      toolGuide,
      setToolGuide,
    }),
    [selectedButtonId, selectButton, toolGuide]
  );

  useEffect(() => {
    if (!map) return;
    const controlSection = new Control({
      element: ref.current ? ref.current : undefined,
    });
    map.addControl(controlSection);
    return () => {
      map.removeControl(controlSection);
    };
  }, [map]);

  return (
    <ControlSectionContext.Provider value={contextValue}>
      <div ref={ref} style={style}>
        {children}
      </div>
    </ControlSectionContext.Provider>
  );
};
